/** Widget paneli tarkibi ustidagi sof amallar (test: layout.test.ts). Hammasi yangi obyekt qaytaradi. */
import { MAX_GLANCE, MAX_ITEMS, type Layout, type WidgetDef, type WidgetItem, type WidgetSize } from "./types";
import type { Me } from "@/features/auth/types";

type Content = Pick<Layout, "items" | "glance">;

export function newWidgetId(type: string, taken: string[], random = () => Math.random().toString(36).slice(2, 8)): string {
  let id = `${type}-${random()}`;
  while (taken.includes(id)) id = `${type}-${random()}`;
  return id;
}

export function canAdd(layout: Content): boolean {
  return layout.items.length < MAX_ITEMS;
}

export function addItem<T extends Content>(layout: T, item: WidgetItem): T {
  if (!canAdd(layout) || layout.items.some((i) => i.id === item.id)) return layout;
  return { ...layout, items: [...layout.items, item] };
}

/** "Qaytarish": olib tashlangan widget o'z joyiga (yuqori panelda turgan bo'lsa -- u yerga ham, joy bo'lsa) */
export function insertItem<T extends Content>(layout: T, item: WidgetItem, index: number, glance: boolean): T {
  if (!canAdd(layout) || layout.items.some((i) => i.id === item.id)) return layout;
  const items = [...layout.items];
  items.splice(Math.min(Math.max(index, 0), items.length), 0, item);
  const showTop = glance && layout.glance.length < MAX_GLANCE;
  return { ...layout, items, glance: showTop ? [...layout.glance, item.id] : layout.glance };
}

export function removeItem<T extends Content>(layout: T, id: string): T {
  return { ...layout, items: layout.items.filter((i) => i.id !== id), glance: layout.glance.filter((g) => g !== id) };
}

/** Sudrab tashlash: `id` ni `overId` turgan joyga */
export function moveItem<T extends Content>(layout: T, id: string, overId: string): T {
  const from = layout.items.findIndex((i) => i.id === id);
  const to = layout.items.findIndex((i) => i.id === overId);
  if (from < 0 || to < 0 || from === to) return layout;
  const items = [...layout.items];
  const [moved] = items.splice(from, 1);
  items.splice(to, 0, moved);
  return { ...layout, items };
}

export function resizeItem<T extends Content>(layout: T, id: string, size: WidgetSize): T {
  return { ...layout, items: layout.items.map((i) => (i.id === id ? { ...i, size } : i)) };
}

export function configureItem<T extends Content>(layout: T, id: string, config: unknown): T {
  return { ...layout, items: layout.items.map((i) => (i.id === id ? { ...i, config } : i)) };
}

export function inGlance(layout: Content, id: string): boolean {
  return layout.glance.includes(id);
}

/** Yuqori panelga qo'yish mumkinmi (allaqachon turgan bo'lsa -- olib tashlash har doim mumkin) */
export function canToggleGlance(layout: Content, id: string): boolean {
  return inGlance(layout, id) || layout.glance.length < MAX_GLANCE;
}

export function toggleGlance<T extends Content>(layout: T, id: string): T {
  if (inGlance(layout, id)) return { ...layout, glance: layout.glance.filter((g) => g !== id) };
  if (!canToggleGlance(layout, id) || !layout.items.some((i) => i.id === id)) return layout;
  return { ...layout, glance: [...layout.glance, id] };
}

/** Yuqori paneldagi widgetlar -- panel tartibida */
export function glanceItems(layout: Content): WidgetItem[] {
  return layout.items.filter((i) => layout.glance.includes(i.id));
}

/**
 * Serverdan kelgan tarkib -> ko'rsatiladigan: noma'lum yoki ochiq bo'lmagan tur tashlanadi, qo'llanmaydigan o'lcham --
 * standart, sozlama -- parseConfig orqali. Server buni tekshiradi; bu -- eski/yangi versiyalar orasida himoya.
 */
export function normalize<T extends Content>(layout: T, defs: Record<string, WidgetDef>, me: Me): T {
  const items: WidgetItem[] = [];
  for (const item of layout.items) {
    const def = defs[item.type];
    if (!def || !def.available(me)) continue;
    const size = def.sizes.includes(item.size) ? item.size : def.defaultSize;
    items.push({ ...item, size, config: def.parseConfig(item.config, me) });
  }
  const ids = new Set(items.map((i) => i.id));
  return { ...layout, items, glance: layout.glance.filter((g) => ids.has(g)).slice(0, MAX_GLANCE) };
}
